import {
  AuthUserPermission,
  DetailModel,
  FieldModel,
  PracticeTemplate,
  PracticeTemplateDetail,
  ProgramBenchmarkName,
  ProgramTask,
  ResourceDetail,
  TaskDataTraitStatus,
} from '@cibo/core'
import { LandThumbnail } from '@cibo/landmanager'
import { Can, usePermissions } from '@cibo/profile'
import BugReportOutlinedIcon from '@mui/icons-material/BugReportOutlined'
import GridViewIcon from '@mui/icons-material/GridView'
import { IconButton, MenuItem, Paper, Select, Stack, Typography } from '@mui/material'
import { Box } from '@mui/system'
import {
  GridCellParams,
  GridColumnHeaderParams,
  GridRenderCellParams,
  MuiEvent,
  useGridApiRef,
} from '@mui/x-data-grid-pro'
import { useSnackbar } from 'notistack'
import { map, pipe, prop, sort, uniq, uniqBy } from 'ramda'
import { KeyboardEvent, MouseEvent, useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { InfoNeededCorner } from '../../../../components/TraitTable/InfoNeededCorner'
import { useProgramEngagementContext } from '../../../../context'
import { useProgramModel, useUpdateEngagementTaskData } from '../../../../queries'
import { DebugDetailResetter } from '../../Tasks/BenchmarkStatus/DebugDetailResetter'
import { DataEntrySchemaEditingProvider } from '../../Tasks/DataEntry/DataEntrySchemaEditingProvider'
import { ApplyTemplateWarning } from './ApplyTemplateWarning'
import { TaskEditorTableCell } from './TaskEditorTableCell'
import { TemplateEditorModal } from './TemplateEditorModal/TemplateEditorModal'
import { TemplateMenu } from './TemplateMenu/TemplateMenu'
import { TraitDataGrid } from './TraitDataGrid'
import { useTraitColumns } from './useTraitColumns'

type CopiedData =
  | { type: 'cell'; fieldId: string; traitId: string; year: number; detail: DetailModel }
  | {
      type: 'row'
      fieldId: string
      traitId: string
      years: Array<{ year: number; detail: DetailModel }>
    }
  | {
      type: 'column'
      fieldId: string
      year: number
      traits: Array<{ traitId: string; detail: DetailModel }>
    }

const removePastingKeys = (keys: string[]) => (prev: Set<string>) => {
  const next = new Set(prev)
  keys.forEach(k => next.delete(k))
  return next
}

export const TaskEditorTable = ({
  task,
}: {
  task: ProgramTask<{ benchmarkName: ProgramBenchmarkName; traits: TaskDataTraitStatus[] }>
}) => {
  const { engagement, fieldModels } = useProgramEngagementContext()
  const programModel = useProgramModel({ programId: engagement.data?.programId })
  const benchmark = programModel.data?.benchmarks?.find(
    ({ benchmark }) => benchmark === task.rule.params?.benchmarkName
  )
  const { t } = useTranslation(['traits', '@cibo/programs/TaskEditorTable'])
  const { enqueueSnackbar } = useSnackbar()
  const { isDebugUser } = usePermissions()

  const [selectedFieldId, setSelectedFieldId] = useState(fieldModels.data?.items?.[0]?.id ?? null)
  const gridApiRef = useGridApiRef()
  const columnKeyHandlerRef = useRef<(e: globalThis.KeyboardEvent) => void>()

  const [templateMenuAnchor, setTemplateMenuAnchor] = useState<HTMLElement | null>(null)
  const [editorModalOpen, setEditorModalOpen] = useState(false)
  const [editorInitialTemplateId, setEditorInitialTemplateId] = useState<string | undefined>()
  const [applyWarningOpen, setApplyWarningOpen] = useState(false)
  const [pendingApply, setPendingApply] = useState<{
    template: PracticeTemplate
    applyableDetails: PracticeTemplateDetail[]
    skippedTraitIds: string[]
    field: FieldModel
  } | null>(null)
  const [copied, setCopied] = useState<CopiedData | null>(null)
  const [pastingCells, setPastingCells] = useState<Set<string>>(new Set())

  const updateEngagementTaskData = useUpdateEngagementTaskData({
    engagementId: engagement.data?.id,
    taskId: task.id,
  })

  const selectedField = fieldModels.data?.items?.find(f => f.id === selectedFieldId)

  // Build columns from unique years in benchmark.details
  const years: string[] = pipe(
    map(prop('year')),
    // @ts-ignore typing pipe is nearly impossible
    uniq,
    sort((a: number | undefined, b: number | undefined) => (a ?? 0) - (b ?? 0)),
    map(String)
  )(benchmark?.details ?? []) as string[]

  // Build rows from unique traitIds in benchmark.details
  const rows = pipe(
    // @ts-ignore typing pipe is nearly impossible
    uniqBy(prop('traitId')),
    map((detail: ResourceDetail) => {
      const row: { id: string; traitId: string; [key: string]: any } = {
        id: detail.traitId,
        traitId: detail.traitId,
      }
      ;(benchmark?.details ?? []).forEach(d => {
        if (d.traitId === detail.traitId) {
          row[String(d.year)] = selectedField?.findDetail({
            traitId: d.traitId,
            year: d.year,
          })
        }
      })
      return row
    })
  )(benchmark?.details ?? []) as { id: string; traitId: string; [key: string]: any }[]

  const handleOpenTemplateMenu = (event: MouseEvent<HTMLButtonElement>) => {
    setTemplateMenuAnchor(event.currentTarget)
  }

  const handleSelectTemplate = (template: PracticeTemplate, fieldIds: string[]) => {
    const fieldId = fieldIds[0]
    if (!fieldId) return
    const field = fieldModels.data?.items?.find(f => f.id === fieldId)
    if (!field) return
    setSelectedFieldId(fieldId)
    const nonEmptyDetails = template.details.filter(
      d => d.input !== undefined || d.result !== undefined || d.value !== undefined
    )
    const skipped: string[] = []
    const applyableDetails = nonEmptyDetails.filter(d => {
      const existing = field.findDetail({ traitId: d.traitId, year: d.year })
      if (existing?.immutable) {
        if (!skipped.includes(d.traitId)) skipped.push(d.traitId)
        return false
      }
      return true
    })

    const pending = { template, applyableDetails, skippedTraitIds: skipped, field }

    const fieldHasData = nonEmptyDetails.some(d =>
      field.findDetail({ traitId: d.traitId, year: d.year })
    )

    if (fieldHasData) {
      setPendingApply(pending)
      setApplyWarningOpen(true)
    } else {
      applyTemplate(pending)
    }
  }

  const applyTemplate = async (pending: {
    template: PracticeTemplate
    applyableDetails: PracticeTemplateDetail[]
    skippedTraitIds: string[]
    field: FieldModel
  }) => {
    const { applyableDetails, skippedTraitIds: skipped, field } = pending

    try {
      await updateEngagementTaskData.mutateAsync({
        updates: {
          fieldDetails: [
            {
              resourceId: field.resourceId,
              details: applyableDetails.map(d => ({
                traitId: d.traitId,
                year: d.year,
                ...(d.input !== undefined ? { input: d.input } : {}),
                ...(d.result !== undefined ? { result: d.result } : {}),
                ...(d.value !== undefined ? { value: d.value } : {}),
              })) as any[],
            },
          ],
        },
      })

      if (skipped.length > 0) {
        enqueueSnackbar(
          t('applyTemplatePartialSuccess', {
            ns: '@cibo/programs/TemplateEditorModal',
            count: skipped.length,
          }),
          { variant: 'warning' }
        )
      } else {
        enqueueSnackbar(t('applyTemplateSuccess', { ns: '@cibo/programs/TemplateEditorModal' }), {
          variant: 'success',
        })
      }
    } catch {
      enqueueSnackbar(t('applyTemplateError', { ns: '@cibo/programs/TemplateEditorModal' }), {
        variant: 'error',
      })
    }

    setApplyWarningOpen(false)
    setPendingApply(null)
  }

  const handleConfirmApply = () => {
    if (pendingApply) applyTemplate(pendingApply)
  }

  const handleOpenNewTemplate = () => {
    setEditorInitialTemplateId(undefined)
    setEditorModalOpen(true)
  }

  const handleEditTemplate = (templateId: string) => {
    setEditorInitialTemplateId(templateId)
    setEditorModalOpen(true)
  }

  const handleCellKeyDown = useCallback(
    // disabled at 23 (visual) 34 (logical)
    // eslint-disable-next-line visual/complexity, complexity
    (params: GridCellParams, event: MuiEvent<KeyboardEvent>) => {
      const traitId = String(params.id)
      const isRowHeader = params.field === 'traitId'
      const year = isRowHeader ? 0 : Number(params.field)

      if ((event.ctrlKey || event.metaKey) && event.key === 'c') {
        event.defaultMuiPrevented = true
        if (isRowHeader) {
          const yearDetails = years
            .map(y => ({
              year: Number(y),
              detail: selectedField?.findDetail({ traitId, year: Number(y) }),
            }))
            .filter((x): x is { year: number; detail: DetailModel } => x.detail != null)
          if (yearDetails.length)
            setCopied({ type: 'row', fieldId: selectedFieldId ?? '', traitId, years: yearDetails })
        } else {
          const detail = selectedField?.findDetail({ traitId, year })
          if (
            detail &&
            (detail.input !== undefined ||
              detail.result !== undefined ||
              (detail as any).value !== undefined)
          ) {
            setCopied({
              type: 'cell',
              fieldId: selectedFieldId ?? '',
              traitId,
              year,
              detail: detail as DetailModel,
            })
          }
        }
        return
      }

      if ((event.ctrlKey || event.metaKey) && event.key === 'v') {
        event.defaultMuiPrevented = true
        if (!copied || !selectedField) return
        if (isRowHeader) {
          if (copied.type !== 'row') return
          if (copied.traitId !== traitId) {
            enqueueSnackbar(t('pasteMismatchedTraits', { ns: '@cibo/programs/TaskEditorTable' }), {
              variant: 'error',
            })
            return
          }
          const details = copied.years
            .filter(({ year }) => !selectedField.findDetail({ traitId, year })?.immutable)
            .map(({ year, detail }) => ({
              traitId,
              year,
              ...(detail.input !== undefined ? { input: detail.input } : {}),
              ...(detail.result !== undefined ? { result: detail.result } : {}),
              ...((detail as any).value !== undefined ? { value: (detail as any).value } : {}),
            })) as any[]
          if (!details.length) {
            enqueueSnackbar(t('pasteAllLocked', { ns: '@cibo/programs/TaskEditorTable' }), {
              variant: 'error',
            })
            return
          }
          const keys = (details as Array<{ traitId: string; year: number }>).map(
            d => `${d.traitId}:${d.year}`
          )
          setPastingCells(prev => {
            const next = new Set(prev)
            keys.forEach(k => next.add(k))
            return next
          })
          updateEngagementTaskData
            .mutateAsync({
              updates: { fieldDetails: [{ resourceId: selectedField.resourceId, details }] },
            })
            .then(() => {
              copied.years.forEach(({ year, detail }) => {
                gridApiRef.current?.updateRows([{ id: traitId, [String(year)]: detail }])
              })
              setPastingCells(removePastingKeys(keys))
            })
            .catch((err: any) => {
              setPastingCells(removePastingKeys(keys))
              enqueueSnackbar(
                err?.response.data.message ||
                  t('pasteError', { ns: '@cibo/programs/TaskEditorTable' }),
                { variant: 'error' }
              )
            })
        } else {
          if (copied.type === 'column') {
            // paste the copied column into the target year (the year of the focused cell)
            const targetYear = year
            const immutableCount = copied.traits.filter(
              ({ traitId: t }) =>
                selectedField.findDetail({ traitId: t, year: targetYear })?.immutable
            ).length
            const details = copied.traits
              .filter(
                ({ traitId: t }) =>
                  !selectedField.findDetail({ traitId: t, year: targetYear })?.immutable
              )
              .map(({ traitId: t, detail }) => ({
                traitId: t,
                year: targetYear,
                ...(detail.input !== undefined ? { input: detail.input } : {}),
                ...(detail.result !== undefined ? { result: detail.result } : {}),
                ...((detail as any).value !== undefined ? { value: (detail as any).value } : {}),
              })) as any[]
            if (!details.length) {
              enqueueSnackbar(t('pasteAllLocked', { ns: '@cibo/programs/TaskEditorTable' }), {
                variant: 'error',
              })
              return
            }
            if (immutableCount > 0) {
              enqueueSnackbar(
                t('pastePartialLocked', {
                  count: immutableCount,
                  ns: '@cibo/programs/TaskEditorTable',
                }),
                {
                  variant: 'warning',
                }
              )
            }
            const keys = (details as Array<{ traitId: string; year: number }>).map(
              d => `${d.traitId}:${d.year}`
            )
            setPastingCells(prev => {
              const next = new Set(prev)
              keys.forEach(k => next.add(k))
              return next
            })
            updateEngagementTaskData
              .mutateAsync({
                updates: { fieldDetails: [{ resourceId: selectedField.resourceId, details }] },
              })
              .then(() => {
                copied.traits.forEach(({ traitId: t, detail }) => {
                  gridApiRef.current?.updateRows([{ id: t, [String(targetYear)]: detail }])
                })
                setPastingCells(removePastingKeys(keys))
              })
              .catch((err: any) => {
                setPastingCells(removePastingKeys(keys))
                enqueueSnackbar(
                  err?.response.data.message ||
                    t('pasteError', { ns: '@cibo/programs/TaskEditorTable' }),
                  { variant: 'error' }
                )
              })
            return
          }
          if (copied.type !== 'cell') return
          if (copied.traitId !== traitId) {
            enqueueSnackbar(t('pasteMismatchedTraits', { ns: '@cibo/programs/TaskEditorTable' }), {
              variant: 'error',
            })
            return
          }
          if (selectedField.findDetail({ traitId, year })?.immutable) {
            enqueueSnackbar(
              t('cannotPasteLocked', { ns: '@cibo/programs/TaskEditorTable', count: 1 }),
              {
                variant: 'error',
              }
            )
            return
          }
          const key = `${traitId}:${year}`
          setPastingCells(prev => new Set(prev).add(key))
          updateEngagementTaskData
            .mutateAsync({
              updates: {
                fieldDetails: [
                  {
                    resourceId: selectedField.resourceId,
                    details: [
                      {
                        traitId,
                        year,
                        ...(copied.detail.input !== undefined
                          ? { input: copied.detail.input }
                          : {}),
                        ...(copied.detail.result !== undefined
                          ? { result: copied.detail.result }
                          : {}),
                        ...((copied.detail as any).value !== undefined
                          ? { value: (copied.detail as any).value }
                          : {}),
                      },
                    ] as any[],
                  },
                ],
              },
            })
            .then(() => {
              gridApiRef.current?.updateRows([{ id: traitId, [params.field]: copied.detail }])
              setPastingCells(removePastingKeys([key]))
            })
            .catch((err: any) => {
              setPastingCells(removePastingKeys([key]))
              enqueueSnackbar(
                err?.response.data.message ||
                  t('pasteError', { ns: '@cibo/programs/TaskEditorTable' }),
                { variant: 'error' }
              )
            })
        }
        return
      }

      if (event.key === 'Escape') setCopied(null)
    },
    [
      copied,
      selectedField,
      selectedFieldId,
      updateEngagementTaskData,
      gridApiRef,
      years,
      enqueueSnackbar,
    ]
  )

  const handleColumnHeaderClick = useCallback((_params: GridColumnHeaderParams) => {}, [])

  // Assigned on every render so the window listener always has a fresh closure
  columnKeyHandlerRef.current = (e: globalThis.KeyboardEvent) => {
    const headerEl = (document.activeElement as HTMLElement)?.closest?.(
      '[role="columnheader"][data-field]'
    )
    const field = headerEl?.getAttribute('data-field')
    if (!field || field === 'traitId') return
    const year = Number(field)

    if ((e.ctrlKey || e.metaKey) && e.key === 'c') {
      e.preventDefault()
      const traits = rows
        .map(r => ({
          traitId: r.traitId,
          detail: selectedField?.findDetail({ traitId: r.traitId, year }),
        }))
        .filter(
          (x): x is { traitId: string; detail: DetailModel } =>
            x.detail != null &&
            (x.detail.input !== undefined ||
              x.detail.result !== undefined ||
              (x.detail as any).value !== undefined)
        )
      if (traits.length) setCopied({ type: 'column', fieldId: selectedFieldId ?? '', year, traits })
      return
    }

    if ((e.ctrlKey || e.metaKey) && e.key === 'v') {
      e.preventDefault()
      if (!copied || copied.type !== 'column' || !selectedField) return
      const targetYear = year
      const immutableCount = copied.traits.filter(
        ({ traitId }) => selectedField.findDetail({ traitId, year: targetYear })?.immutable
      ).length
      const details = copied.traits
        .filter(
          ({ traitId }) => !selectedField.findDetail({ traitId, year: targetYear })?.immutable
        )
        .map(({ traitId, detail }) => ({
          traitId,
          year: targetYear,
          ...(detail.input !== undefined ? { input: detail.input } : {}),
          ...(detail.result !== undefined ? { result: detail.result } : {}),
          ...((detail as any).value !== undefined ? { value: (detail as any).value } : {}),
        })) as any[]
      if (!details.length) {
        enqueueSnackbar(t('pasteAllLocked', { ns: '@cibo/programs/TaskEditorTable' }), {
          variant: 'error',
        })
        return
      }
      if (immutableCount > 0) {
        enqueueSnackbar(
          t('pastePartialLocked', { count: immutableCount, ns: '@cibo/programs/TaskEditorTable' }),
          { variant: 'warning' }
        )
      }
      const keys = (details as Array<{ traitId: string; year: number }>).map(
        d => `${d.traitId}:${d.year}`
      )
      setPastingCells(prev => {
        const next = new Set(prev)
        keys.forEach(k => next.add(k))
        return next
      })
      updateEngagementTaskData
        .mutateAsync({
          updates: { fieldDetails: [{ resourceId: selectedField.resourceId, details }] },
        })
        .then(() => {
          copied.traits.forEach(({ traitId, detail }) => {
            gridApiRef.current?.updateRows([{ id: traitId, [String(targetYear)]: detail }])
          })
          setPastingCells(removePastingKeys(keys))
        })
        .catch((err: any) => {
          setPastingCells(removePastingKeys(keys))

          enqueueSnackbar(
            err?.response.data.message || t('pasteError', { ns: '@cibo/programs/TaskEditorTable' }),
            { variant: 'error' }
          )
        })
      return
    }

    if (e.key === 'Escape') setCopied(null)
  }

  useEffect(() => {
    const handler = (e: globalThis.KeyboardEvent) => columnKeyHandlerRef.current?.(e)
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [])

  const getCellClassName = useCallback(
    (params: GridCellParams) => {
      if (!copied) return ''
      const id = String(params.id)
      const field = params.field
      const isSourceField = selectedFieldId === copied.fieldId
      if (copied.type === 'cell') {
        if (id !== copied.traitId || Number(field) !== copied.year) return ''
        return isSourceField ? 'cell--copied' : 'cell--paste-target'
      }
      if (copied.type === 'row') {
        if (id !== copied.traitId || field === 'traitId') return ''
        if (!copied.years.some(y => y.year === Number(field))) return ''
        return isSourceField ? 'cell--copied' : 'cell--paste-target'
      }
      if (copied.type === 'column') {
        if (field === 'traitId' || Number(field) !== copied.year) return ''
        if (!copied.traits.some(t => t.traitId === id)) return ''
        return isSourceField ? 'cell--copied' : 'cell--paste-target'
      }
      return ''
    },
    [copied, selectedFieldId]
  )

  const updateRowValue = (traitId: string, year: string) => {
    const updatedValue = selectedField?.findDetail({
      traitId,
      year: Number(year),
    })
    gridApiRef.current?.updateRows([
      {
        id: traitId,
        [year]: updatedValue,
      },
    ])
  }

  const renderYearCell = useCallback(
    (year: string) => (params: GridRenderCellParams) =>
      (
        <TaskEditorTableCell
          task={task}
          selectedField={selectedField}
          onSaveSuccess={() => updateRowValue(params.row.traitId, year)}
          onDeleteSuccess={() => updateRowValue(params.row.traitId, year)}
          isLoading={pastingCells.has(`${params.row.traitId}:${year}`)}
          {...params}
        />
      ),
    [task, selectedField, updateRowValue, pastingCells]
  )

  const columns = useTraitColumns({
    traitIdHeaderName: 'Past Practices',
    years,
    renderYearCell,
  })

  return (
    <DataEntrySchemaEditingProvider>
      <Stack spacing={2}>
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Stack>
            <Stack direction="row" spacing={2} justifyContent="space-between" alignItems="center">
              <Stack>
                <Typography variant="h6">Field Practices</Typography>
                <Typography>Enter historical practices for each field</Typography>
              </Stack>
              <Stack direction="row" spacing={2} alignItems="center">
                {isDebugUser && (
                  <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                    <BugReportOutlinedIcon
                      fontSize="small"
                      color="error"
                      sx={{
                        position: 'absolute',
                        top: -7,
                        left: -7,
                        zIndex: 1,
                        pointerEvents: 'none',
                      }}
                    />
                    <IconButton
                      size="small"
                      data-testid="template-menu-button"
                      onClick={handleOpenTemplateMenu}
                      aria-label={t('openTemplateMenu', {
                        ns: '@cibo/programs/TemplateEditorModal',
                      })}
                      sx={{
                        borderColor: theme => theme.palette.error.main,
                        borderWidth: 1,
                        borderStyle: 'solid',
                      }}
                    >
                      <GridViewIcon />
                    </IconButton>
                  </Box>
                )}
                <Select
                  value={
                    fieldModels.data?.items?.find(f => f.id === selectedFieldId)?.id ?? 'allFields'
                  }
                  onChange={e => {
                    const fieldId = e.target.value
                    if (fieldId !== 'allFields') {
                      setSelectedFieldId(fieldId)
                    }
                  }}
                  size="small"
                  sx={{ minWidth: 216 }}
                >
                  {fieldModels.data?.items?.map(field => (
                    <MenuItem key={field.id} value={field.id}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <LandThumbnail field={field} width={32} height={32} />
                        <Stack>
                          <Typography>{field.name}</Typography>
                          <Typography variant="caption">
                            {t('acres', {
                              count: field.acres?.toFixed(1) ?? 0,
                              ns: '@cibo/landmanager/components',
                            })}
                          </Typography>
                        </Stack>
                      </Stack>
                    </MenuItem>
                  ))}
                </Select>
              </Stack>{' '}
            </Stack>
            <Stack direction="row" alignItems="center" pt={2} justifyContent="flex-end">
              <Box
                sx={{
                  width: 16,
                  height: 16,
                  position: 'relative',
                }}
              >
                <InfoNeededCorner />
              </Box>
              <Typography variant="caption" paddingLeft={0.5}>
                {t('infoNeeded', { ns: '@cibo/landmanager/components' })}
              </Typography>
            </Stack>
            <TraitDataGrid
              apiRef={gridApiRef}
              rows={rows}
              columns={columns}
              onCellKeyDown={handleCellKeyDown}
              getCellClassName={getCellClassName}
              onColumnHeaderClick={handleColumnHeaderClick}
            />
          </Stack>
        </Paper>
        <Can useAny={[AuthUserPermission.DEBUG_USER]}>
          <DebugDetailResetter accordion benchmark={benchmark} />
        </Can>
      </Stack>

      <TemplateMenu
        anchorEl={templateMenuAnchor}
        onClose={() => setTemplateMenuAnchor(null)}
        task={task}
        onSelectTemplate={handleSelectTemplate}
        onNewTemplate={handleOpenNewTemplate}
        onEditTemplate={handleEditTemplate}
        selectedFieldId={selectedFieldId}
      />

      <TemplateEditorModal
        open={editorModalOpen}
        onClose={() => setEditorModalOpen(false)}
        task={task}
        initialTemplateId={editorInitialTemplateId}
      />

      <ApplyTemplateWarning
        open={applyWarningOpen}
        template={pendingApply?.template ?? null}
        onConfirm={handleConfirmApply}
        onCancel={() => {
          setApplyWarningOpen(false)
          setPendingApply(null)
        }}
        isApplying={updateEngagementTaskData.isPending}
        immutableTraitIds={pendingApply?.skippedTraitIds ?? []}
      />
    </DataEntrySchemaEditingProvider>
  )
}
